import React, {FC, useCallback} from "react";
import {ModalPropsDTO, ModalsProviderTypes} from "./ModalsProvider.types";
import {ModalsProviderContext} from "./ModalsProvider.context";
import {ModalProps} from "../../components/Modal/Modal.types";
import Modal from "../../components/Modal";
import {generateUUIDV4} from "../../common/functions";
import {Button} from "../../components";

const ModalsProvider: FC<ModalsProviderTypes> = ({children}) => {
    const [modals, setModals] = React.useState<{ [key: string]: ModalProps }>({});

    const openModal = useCallback((props: ModalPropsDTO): string => {
        const uuid = generateUUIDV4();
        setModals((prev) => ({
            ...prev,
            [uuid]: {
                ...props,
                open: true,
                onClose: () => {
                    closeModal(uuid);
                },
                onCloseAnimationEnd: () => {
                    setModals((prev) => {
                        const newModals = {...prev};
                        delete newModals[uuid];
                        return newModals;
                    });
                },
            },
        }));

        return uuid;
    }, []);

    const closeModal = useCallback((uuid: string) => {
        setModals((prev) => ({
            ...prev,
            [uuid]: {
                ...prev[uuid],
                open: false,
            },
        }));
    }, []);

    const closeAllModals = () => {
        setModals((prevState) => {
            const newModals = {...prevState};
            Object.entries(newModals).forEach(([key, value]) => {
                newModals[key] = {...value, open: false};
            });
            return newModals;
        });
    };

    const openConfirmationModal = (props: {
        title: string;
        message: string;
        onConfirm: () => void;
        onCancel?: () => void;
        confirmText?: string;
        cancelText?: string;
    }) => {
        const defaultProps = {
            title: "Confirmation",
            message: "Are you sure you want to do this?",
            confirmText: "Confirm",
            cancelText: "Cancel",
        };

        const {title, message, confirmText, cancelText} = {
            ...defaultProps,
            ...props,
        };

        const onConfirm = (id: string) => {
            props.onConfirm();
            closeModal(id);
        };

        const onCancel = (id: string) => {
            props.onCancel?.();
            closeModal(id);
        };

        const id = openModal({
            children: (
                <>
                    <Modal.Header title={title} description={message}/>
                    <Modal.Footer>
                        <Button color="link" variant="outlined"
                                onClick={() => onCancel(id)}>{cancelText}</Button>
                        <Button variant={"outlined"} color={"secondary"}
                                onClick={() => onConfirm(id)}>{confirmText}</Button>
                    </Modal.Footer>
                </>
            ),
        });
    };

    return (
        <>
            <ModalsProviderContext.Provider
                value={{openModal, closeModal, closeAllModals, openConfirmationModal}}
            >
                {children}
                {Object.entries(modals).map(([key, modal]) => {
                    return <Modal {...modal} key={key}/>;
                })}
            </ModalsProviderContext.Provider>
        </>
    );
};

export default ModalsProvider;
